import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Autocomplete,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  Schedule as ScheduleIcon,
  Notes as NotesIcon
} from '@mui/icons-material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { it } from 'date-fns/locale';
import { Booking, ExecutionUnit, HealthcareService, Doctor, Availability } from '../../types';
import { bookingService } from '../../services/bookingService';
import { executionUnitService } from '../../services/executionUnitService';
import { diaryService } from '../../services/diaryService';
import './BookingForm.css';

interface BookingFormProps {
  booking?: Booking | null;
  onSave: (booking: Booking) => void;
  onCancel: () => void;
  open?: boolean;
}

interface PatientData {
  fiscalCode: string;
  name: string;
  surname: string;
  birthDate: string;
  phone?: string;
  email?: string;
}

const steps = ['Dati Paziente', 'Servizio e Unità', 'Data e Ora', 'Conferma'];

const BookingForm: React.FC<BookingFormProps> = ({ 
  booking, 
  onSave, 
  onCancel, 
  open = true 
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form data
  const [formData, setFormData] = useState<Partial<Booking>>({
    id: booking?.id || '',
    patientName: booking?.patientName || '',
    patientFiscalCode: booking?.patientFiscalCode || '',
    patientPhone: booking?.patientPhone || '',
    patientEmail: booking?.patientEmail || '',
    serviceName: booking?.serviceName || '',
    serviceId: booking?.serviceId || '',
    executionUnitId: booking?.executionUnitId || '',
    doctorId: booking?.doctorId || '',
    appointmentDate: booking?.appointmentDate || '',
    appointmentTime: booking?.appointmentTime || '',
    status: booking?.status || 'pending',
    notes: booking?.notes || '',
    priority: booking?.priority || 'normal'
  });

  // Patient data
  const [patientData, setPatientData] = useState<PatientData>({
    fiscalCode: booking?.patientFiscalCode || '',
    name: booking?.patientName?.split(' ')[0] || '',
    surname: booking?.patientName?.split(' ').slice(1).join(' ') || '',
    birthDate: '',
    phone: booking?.patientPhone || '',
    email: booking?.patientEmail || ''
  });

  // Options data
  const [executionUnits, setExecutionUnits] = useState<ExecutionUnit[]>([]);
  const [services, setServices] = useState<HealthcareService[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [availabilities, setAvailabilities] = useState<Availability[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    booking?.appointmentDate ? new Date(booking.appointmentDate) : null
  );
  const [selectedTime, setSelectedTime] = useState<Date | null>(
    booking?.appointmentTime ? new Date(`2000-01-01T${booking.appointmentTime}`) : null
  );

  // Patient search
  const [searchingPatient, setSearchingPatient] = useState(false);
  const [patientFound, setPatientFound] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (formData.executionUnitId) {
      loadServices(formData.executionUnitId);
      loadDoctors(formData.executionUnitId);
    }
  }, [formData.executionUnitId]);

  useEffect(() => {
    if (formData.serviceId && formData.executionUnitId && selectedDate) {
      loadAvailabilities();
    }
  }, [formData.serviceId, formData.executionUnitId, selectedDate]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const units = await executionUnitService.getExecutionUnits();
      setExecutionUnits(units);
    } catch (err) {
      setError('Errore nel caricamento dei dati iniziali');
    } finally {
      setLoading(false);
    }
  };

  const loadServices = async (unitId: string) => {
    try {
      const servicesData = await diaryService.getServicesByUnit(unitId);
      setServices(servicesData);
    } catch (err) {
      console.error('Errore nel caricamento dei servizi:', err);
    }
  };

  const loadDoctors = async (unitId: string) => {
    try {
      const doctorsData = await diaryService.getDoctorsByUnit(unitId);
      setDoctors(doctorsData);
    } catch (err) {
      console.error('Errore nel caricamento dei medici:', err);
    }
  };

  const loadAvailabilities = async () => {
    if (!formData.serviceId || !formData.executionUnitId || !selectedDate) return;
    
    try {
      const availData = await diaryService.getAvailabilities({
        serviceId: formData.serviceId,
        executionUnitId: formData.executionUnitId,
        date: selectedDate.toISOString().split('T')[0]
      });
      setAvailabilities(availData);
    } catch (err) {
      console.error('Errore nel caricamento delle disponibilità:', err);
    }
  };

  const searchPatient = async (fiscalCode: string) => {
    if (!fiscalCode || fiscalCode.length < 16) return;
    
    setSearchingPatient(true);
    try {
      // Simula ricerca paziente da RECUP
      // In realtà dovrebbe chiamare un endpoint RECUP per cercare il paziente
      const patient = await bookingService.searchPatient(fiscalCode);
      if (patient) {
        setPatientData({
          fiscalCode: patient.fiscalCode,
          name: patient.name,
          surname: patient.surname,
          birthDate: patient.birthDate,
          phone: patient.phone,
          email: patient.email
        });
        setFormData(prev => ({
          ...prev,
          patientName: `${patient.name} ${patient.surname}`,
          patientFiscalCode: patient.fiscalCode,
          patientPhone: patient.phone,
          patientEmail: patient.email
        }));
        setPatientFound(true);
        setSuccess('Paziente trovato nel sistema RECUP');
      } else {
        setPatientFound(false);
        setError('Paziente non trovato nel sistema RECUP');
      }
    } catch (err) {
      setError('Errore nella ricerca del paziente');
      setPatientFound(false);
    } finally {
      setSearchingPatient(false);
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0: // Dati Paziente
        if (!patientData.fiscalCode || !patientData.name || !patientData.surname) {
          setError('Compilare tutti i campi obbligatori del paziente');
          return false;
        }
        break;
      case 1: // Servizio e Unità
        if (!formData.executionUnitId || !formData.serviceId) {
          setError('Selezionare unità erogante e servizio');
          return false;
        }
        break;
      case 2: // Data e Ora
        if (!selectedDate || !selectedTime) {
          setError('Selezionare data e ora dell\'appuntamento');
          return false;
        }
        break;
    }
    setError(null);
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) return;

    setLoading(true);
    setError(null);
    
    try {
      const bookingData: Booking = {
        ...formData,
        patientName: `${patientData.name} ${patientData.surname}`,
        patientFiscalCode: patientData.fiscalCode,
        patientPhone: patientData.phone,
        patientEmail: patientData.email,
        appointmentDate: selectedDate!.toISOString().split('T')[0],
        appointmentTime: selectedTime!.toTimeString().split(' ')[0].substring(0, 5),
        createdAt: booking?.createdAt || new Date(),
        updatedAt: new Date()
      } as Booking;

      if (booking?.id) {
        await bookingService.updateBooking(booking.id, bookingData);
        setSuccess('Prenotazione aggiornata con successo');
      } else {
        await bookingService.createBooking(bookingData);
        setSuccess('Prenotazione creata con successo');
      }

      setTimeout(() => {
        onSave(bookingData);
      }, 1500);
    } catch (err) {
      setError('Errore nel salvataggio della prenotazione: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box className="step-content">
            <Typography variant="h6" gutterBottom className="step-title">
              <PersonIcon /> Dati del Paziente
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Codice Fiscale"
                  value={patientData.fiscalCode}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    setPatientData(prev => ({ ...prev, fiscalCode: value }));
                    if (value.length === 16) {
                      searchPatient(value);
                    }
                  }}
                  inputProps={{ maxLength: 16 }}
                  required
                  InputProps={{
                    endAdornment: searchingPatient ? <CircularProgress size={20} /> : null
                  }}
                  helperText="Inserire il codice fiscale per cercare il paziente nel sistema RECUP"
                />
              </Grid>
              {patientFound && (
                <Grid item xs={12}>
                  <Alert severity="success">
                    Paziente trovato nel sistema RECUP
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nome"
                  value={patientData.name}
                  onChange={(e) => setPatientData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  disabled={patientFound}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Cognome"
                  value={patientData.surname}
                  onChange={(e) => setPatientData(prev => ({ ...prev, surname: e.target.value }))}
                  required
                  disabled={patientFound}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data di Nascita"
                  value={patientData.birthDate}
                  onChange={(e) => setPatientData(prev => ({ ...prev, birthDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  disabled={patientFound}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Telefono"
                  value={patientData.phone}
                  onChange={(e) => setPatientData(prev => ({ ...prev, phone: e.target.value }))}
                  disabled={patientFound}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  value={patientData.email}
                  onChange={(e) => setPatientData(prev => ({ ...prev, email: e.target.value }))}
                  disabled={patientFound}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box className="step-content">
            <Typography variant="h6" gutterBottom className="step-title">
              <HospitalIcon /> Servizio e Unità Erogante
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Unità Erogante</InputLabel>
                  <Select
                    value={formData.executionUnitId || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      executionUnitId: e.target.value,
                      serviceId: '', // Reset service when unit changes
                      doctorId: '' // Reset doctor when unit changes
                    }))}
                  >
                    {executionUnits.map((unit) => (
                      <MenuItem key={unit.id} value={unit.id}>
                        <Box>
                          <Typography variant="body1">{unit.name}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {unit.address}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required disabled={!formData.executionUnitId}>
                  <InputLabel>Servizio</InputLabel>
                  <Select
                    value={formData.serviceId || ''}
                    onChange={(e) => {
                      const selectedService = services.find(s => s.id === e.target.value);
                      setFormData(prev => ({ 
                        ...prev, 
                        serviceId: e.target.value,
                        serviceName: selectedService?.name || ''
                      }));
                    }}
                  >
                    {services.map((service) => (
                      <MenuItem key={service.id} value={service.id}>
                        <Box>
                          <Typography variant="body1">{service.name}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Durata: {service.duration} min - Costo: €{service.cost}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth disabled={!formData.executionUnitId}>
                  <InputLabel>Medico (Opzionale)</InputLabel>
                  <Select
                    value={formData.doctorId || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, doctorId: e.target.value }))}
                  >
                    <MenuItem value="">Nessuna preferenza</MenuItem>
                    {doctors.map((doctor) => (
                      <MenuItem key={doctor.id} value={doctor.id}>
                        <Box>
                          <Typography variant="body1">
                            Dr. {doctor.firstName} {doctor.lastName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {doctor.specialization}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Priorità</InputLabel>
                  <Select
                    value={formData.priority || 'normal'}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                  >
                    <MenuItem value="low">Bassa</MenuItem>
                    <MenuItem value="normal">Normale</MenuItem>
                    <MenuItem value="high">Alta</MenuItem>
                    <MenuItem value="urgent">Urgente</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
            <Box className="step-content">
              <Typography variant="h6" gutterBottom className="step-title">
                <ScheduleIcon /> Data e Ora dell'Appuntamento
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Data Appuntamento"
                    value={selectedDate}
                    onChange={(newValue) => setSelectedDate(newValue)}
                    minDate={new Date()}
                    renderInput={(params) => <TextField {...params} fullWidth required />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TimePicker
                    label="Ora Appuntamento"
                    value={selectedTime}
                    onChange={(newValue) => setSelectedTime(newValue)}
                    renderInput={(params) => <TextField {...params} fullWidth required />}
                  />
                </Grid>
                {availabilities.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Orari Disponibili:
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {availabilities.map((avail, index) => (
                        <Chip
                          key={index}
                          label={`${avail.startTime} - ${avail.endTime}`}
                          onClick={() => {
                            const time = new Date(`2000-01-01T${avail.startTime}`);
                            setSelectedTime(time);
                          }}
                          color={selectedTime?.toTimeString().substring(0, 5) === avail.startTime ? 'primary' : 'default'}
                          variant={selectedTime?.toTimeString().substring(0, 5) === avail.startTime ? 'filled' : 'outlined'}
                        />
                      ))}
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Note"
                    value={formData.notes || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Inserire eventuali note per l'appuntamento..."
                  />
                </Grid>
              </Grid>
            </Box>
          </LocalizationProvider>
        );

      case 3:
        return (
          <Box className="step-content">
            <Typography variant="h6" gutterBottom className="step-title">
              Riepilogo Prenotazione
            </Typography>
            <Paper elevation={1} className="summary-paper">
              <Box p={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      Dati Paziente
                    </Typography>
                    <Typography variant="body2">
                      <strong>Nome:</strong> {patientData.name} {patientData.surname}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Codice Fiscale:</strong> {patientData.fiscalCode}
                    </Typography>
                    {patientData.phone && (
                      <Typography variant="body2">
                        <strong>Telefono:</strong> {patientData.phone}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      Dettagli Appuntamento
                    </Typography>
                    <Typography variant="body2">
                      <strong>Unità Erogante:</strong> {executionUnits.find(u => u.id === formData.executionUnitId)?.name}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Servizio:</strong> {formData.serviceName}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Data:</strong> {selectedDate?.toLocaleDateString('it-IT')}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Ora:</strong> {selectedTime?.toTimeString().substring(0, 5)}
                    </Typography>
                    {formData.doctorId && (
                      <Typography variant="body2">
                        <strong>Medico:</strong> Dr. {doctors.find(d => d.id === formData.doctorId)?.firstName} {doctors.find(d => d.id === formData.doctorId)?.lastName}
                      </Typography>
                    )}
                    <Typography variant="body2">
                      <strong>Priorità:</strong> {formData.priority === 'low' ? 'Bassa' : formData.priority === 'normal' ? 'Normale' : formData.priority === 'high' ? 'Alta' : 'Urgente'}
                    </Typography>
                  </Grid>
                  {formData.notes && (
                    <>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" color="primary" gutterBottom>
                          Note
                        </Typography>
                        <Typography variant="body2">{formData.notes}</Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      className="booking-form-dialog"
    >
      <DialogTitle className="dialog-title">
        {booking ? 'Modifica Prenotazione' : 'Nuova Prenotazione'}
      </DialogTitle>
      
      <DialogContent className="dialog-content">
        {/* Stepper */}
        <Box mb={3}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Step Content */}
        {renderStepContent(activeStep)}
      </DialogContent>

      <DialogActions className="dialog-actions">
        <Button
          onClick={onCancel}
          startIcon={<CancelIcon />}
          disabled={loading}
        >
          Annulla
        </Button>
        
        {activeStep > 0 && (
          <Button
            onClick={handleBack}
            disabled={loading}
          >
            Indietro
          </Button>
        )}
        
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading}
          >
            Avanti
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={loading}
          >
            {loading ? 'Salvando...' : 'Salva Prenotazione'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BookingForm;